import _isEmpty from 'lodash/isEmpty';
import { purgeStoredState } from 'redux-persist';
import { asyncSessionStorage } from 'redux-persist/storages';

import { locationUpdate } from './action-contact-location';
import { contactByEmail } from './action-contact-option';
import { loadChatIneligibleModalContainer } from './action-chat-ineligible-modal';
import { fetchFinalTopicContent } from './action-select-topic';
import poll from '../../lib/poll';
import { nodeChatOnPageValidator } from '../../lib/node-chat-on-page-validator';
import Page from '../../lib/page';
import { modalChannels } from '../constants/UtilVariables';
import fireImpressionFptiForChatLoadError from '../../lib/fire-impression-fpti-for-chat-load-error';
import AsyncHelper from '../../lib/asyncMessageHelper';
import { unreadConversationChecker } from '../../lib/conversationMsgHelper';
import { loadMessageModal, closeModalContainer } from './action-login-modal';
import { postChatBotEligibility } from './action-postchatbot-eligibility';
import l10n from '../../lib/reactPPl10nHelper';
import { trackPage } from '../../lib/analytics-helper';
import { initiateRiskCheck, isLoggedOutToLogin } from './action-step-up';

const modalErrorMsg = l10n.getContent('smarthelp/info.info.title.functionIsUnavailable');

const isLoggedIn = Page.isLoggedIn();

// This logic applies for the chatBot corner case scenario only.
const wasChatBotClicked = (window.sessionStorage && sessionStorage.getItem('chatBotClicked') === 'true');
const wasLiveChatClicked = (window.sessionStorage && sessionStorage.getItem('liveChatClicked') === 'true');

// the Poll function will retry for at least 2 secs based on current retries = 10 and delay = 200 ms
const pollRetries = 10;
const pollDelay = 200;

// Remote config check for chatBot Eligibility API
const chatBotPostServiceEnabled = Page.isChatBotPostServiceEnabled();

function resetChatClicksInSessionStorage() {
  sessionStorage.setItem('chatBotClicked', 'false');
  sessionStorage.setItem('liveChatClicked', 'false');
}

const asyncChatResolver = (topicRefId) => {
  setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);
  // check MMT service; redirect to SMC or open async chat based on response
  return AsyncHelper.rediretPromise(unreadConversationChecker, { topicRefId });
};

export const showChatBotWhenPresentOnPage = (topic = {}, fromChannel) => (dispatch, getState) => {
  const { selectResult: { topicTreeSelectResult } } = getState();
  if (chatBotPostServiceEnabled && _isEmpty(topicTreeSelectResult) && fromChannel !== modalChannels.liveChat) {
    dispatch(fetchFinalTopicContent({ topic, fromChannel }));
  }

  // Pilot user validation
  if (Page.isPilotUser()) {
    const topicRefId = topic.topicRefId ? `${topic.topicRefId}|finalIntent` : 'GENPOOL|contactUsPage';
    return asyncChatResolver(topicRefId);
  }

    // Waits at least 3.20 secs based on current retries = 5 and delay = 200 ms
  return poll(nodeChatOnPageValidator, pollRetries, pollDelay)
      .then(() => {
        // eslint-disable-next-line no-undef
        nodeChat.show(fromChannel, `${topic.topicRefId}|finalIntent`);
      })
      .catch(() => fireImpressionFptiForChatLoadError());
};

function performPostChatBotEligibility(postChatBotResponse) {
  return (dispatch) => {
    if ((postChatBotResponse && !isLoggedIn) || (isLoggedIn && !wasChatBotClicked)) {
      dispatch(postChatBotEligibility(postChatBotResponse));
    } else {
      resetChatClicksInSessionStorage();
      dispatch(postChatBotEligibility({
        postChatBotResponse,
        showChat: showChatBotWhenPresentOnPage,
        reject: loadChatIneligibleModalContainer,
      }));
    }
  };
}

/**
 * Calling postChatBot Eligibility API for checking the eligibility to show/hide chatBot
 */

export const chatBotEligibilityResponse = () => (dispatch, getState) => {
  const state = getState();
  let topic = null;
  let postChatBotResponse;

  if (isLoggedOutToLogin(getState())) {
    topic = state.modal.topic;
  } else {
    topic = state.selectResult.topicTreeSelectResult;
  }

  // from log out to login after clicking on chatbot in final intent page
  if (!_isEmpty(topic)) {
    postChatBotResponse = Page.getChatBotEligibilityParams(topic.topicRefId, 'TOPIC');
  }

  // from log out to login after clicking on chatbot in default page
  if (_isEmpty(topic)) {
    postChatBotResponse = Page.getChatBotEligibilityParams('contactUSpage', 'GENERIC');
  }

  return dispatch(performPostChatBotEligibility(postChatBotResponse));
};

export default function fetchContactUsOnLogin() {
  return (dispatch, getState) => { // eslint-disable-line consistent-return
    const { modal: { fromChannel, topic }, persistEmailForm: { fields }, user: { islocaleChanged },
      smcResultKeeper: { topicTreeSelectResult }, breadCrumb: { items }, persistLocation: { pageName },
      persistedStepUp: { fromChannel: stepUpFromChannel, topic: persistedTopic, anw: { isRedirectedToAnw } } } = getState();


    if (Page.isLoggedIn()) {
      // If isRedirectedToAnw[redux state value] is true then perform step-up
      // This will be when user clicks on back button in ANW page or when redirected from ANW with success or error.
      if (isRedirectedToAnw) {
        return dispatch(initiateRiskCheck(stepUpFromChannel, persistedTopic,
          { isOpenFinalIntentBeforeStepUp: true },
          'second'));
      }
    }

    if (!Page.isLoggedIn()) {
      dispatch(closeModalContainer());
    }

    // Show Error message, if the Lexa service is failed
    if (Page.isLexaPilotFailure() &&
      (fromChannel === modalChannels.liveChat || fromChannel === modalChannels.chatBot || fromChannel === modalChannels.defaultEmail || fromChannel === modalChannels.finalEmail)) {
      // Fetching final topic intent, if the topic select result is persisted
      if (!_isEmpty(topicTreeSelectResult)) {
        dispatch(fetchFinalTopicContent({ topic: topicTreeSelectResult, fromChannel }));
      }
      setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);
      trackPage({ id: 'contactLexaFailureError', link: 'smarthelp-intermediate|lexa-failure' });
      return dispatch(loadMessageModal({ message: modalErrorMsg, channel: fromChannel, topic: topicTreeSelectResult })); // Open Prompt window and show error message
    }

    // when postchatbotAPI remote config is true and not from step-up final intent then perform chatBotEligibility.
    if (chatBotPostServiceEnabled && !stepUpFromChannel) {
      dispatch(chatBotEligibilityResponse());
    }

    // if from logged out to login in from callUs - default contact options.
    if (fromChannel === modalChannels.call && stepUpFromChannel !== modalChannels.call && isLoggedIn && !islocaleChanged) {
      dispatch(locationUpdate({ pageName: 'callUs' }));

      // If from logged out to login in from callUs in final intent
    } else if (fromChannel === modalChannels.call && stepUpFromChannel === modalChannels.call && isLoggedIn && !islocaleChanged) {
      dispatch(initiateRiskCheck(stepUpFromChannel, persistedTopic));

      // Non US chatBot hidden case
    } else if (!Page.getIsChatBotEnabled() && fromChannel === modalChannels.chatBot && wasChatBotClicked) {
      if (!chatBotPostServiceEnabled) {
        dispatch(loadChatIneligibleModalContainer());
        resetChatClicksInSessionStorage();
      }
      if (chatBotPostServiceEnabled && stepUpFromChannel) {
        dispatch(chatBotEligibilityResponse());
      }

      // US chatBot show case
    } else if (Page.getIsChatBotEnabled() && fromChannel === modalChannels.chatBot && wasChatBotClicked) {
      // ToDo:: Remove all remote config checks as we no longer consider remote configs. priority goes to post chatbot eligibilty service.
      if (Page.getIsLogoutChatbotEnabled()) {
        if (isLoggedOutToLogin(getState()) && stepUpFromChannel === 'chat') {
          // perform step-up
          dispatch(initiateRiskCheck(fromChannel, persistedTopic, { isOpenFinalIntentBeforeStepUp: true }));
        } else if (!chatBotPostServiceEnabled) {
          dispatch(showChatBotWhenPresentOnPage(topic, fromChannel)); // Combine with Pilot user checker

          if (!_isEmpty(topic)) {
            dispatch(fetchFinalTopicContent({ topic, fromChannel }));
          }
        }
      } else {
        dispatch(chatBotEligibilityResponse());
        resetChatClicksInSessionStorage();
      }
    } else if (fromChannel === modalChannels.liveChat && wasLiveChatClicked) {
      dispatch(initiateRiskCheck(fromChannel, persistedTopic, { isOpenFinalIntentBeforeStepUp: true }));
    } else if (fromChannel === modalChannels.finalEmail && isLoggedIn && !islocaleChanged) {
      // Pilot user checker for final intent Email form
      if (Page.isPilotUser()) {
        // making chatcard clicked true after pilot user clicks on email icon and log in.
        sessionStorage.setItem('chatCardClicked', 'true');
        dispatch(fetchFinalTopicContent({ topic: topicTreeSelectResult, fromChannel })); // show last final intent when logout to login in the final intent
        asyncChatResolver(`${topicTreeSelectResult.topicRefId}|finalIntent`);
      } else if (!Page.isEmailNotSent()) {
        if (Page.isRedirectToSMC()) {
          setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {
          }), 400);
          window.open(`/smc/compose?topicId=${topicTreeSelectResult.topicId}&breadcrumbs=${items.toString()}`, '_self');
        } else {
          dispatch(locationUpdate({ pageName: 'contactUs' }));
          dispatch(contactByEmail(fields));
        }
      }
    } else if (fromChannel === modalChannels.defaultEmail && isLoggedIn && !islocaleChanged) {
      setTimeout(() => purgeStoredState({ storage: asyncSessionStorage }).then(() => {}), 400);

      // Pilot user checker for login from default Email option
      if (Page.isPilotUser()) {
        // making chatcard clicked true after pilot user clicks on email icon and log in.
        sessionStorage.setItem('chatCardClicked', 'true');
        asyncChatResolver('GENPOOL|contactUsPage');

        // Redirect to Call us page, if the user select Email option in the call us page
        if (pageName === 'callUs') {
          dispatch(locationUpdate({ pageName: 'callUs' }));
        }
      } else if (Page.isRedirectToSMC()) {
        Page.redirectToSMC();
      } else {
        dispatch(locationUpdate({ pageName: 'email' }));
      }
    }
  };
}
